import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import CircularProgress from "@mui/material/CircularProgress"
import CloseIcon from "@mui/icons-material/Close"
import { useUtils } from "hooks/useUtils"

const UploadingBox = () => (
  <Box
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 2,
      background: "#fafafa",
      minHeight: 100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size={24} />
  </Box>
)

const ImageDropZone = ({
  preview,
  onFile,
  onRemove,
  accept,
  dragging,
  setDragging,
  inputRef,
  removeLabel,
  uploading,
}) => (
  <>
    {uploading ? (
      <UploadingBox />
    ) : preview ? (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          background: "#fafafa",
          minHeight: 100,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={preview}
          alt=""
          style={{
            width: "100%",
            height: 100,
            objectFit: "contain",
            borderRadius: 4,
            display: "block",
          }}
        />
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label={removeLabel}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            background: "rgba(255,255,255,0.8)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : (
      <Box
        sx={{
          border: dragging ? "2px dashed #4A90D9" : "2px dashed #aaa",
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          background: dragging ? "#f0f7ff" : "#fafafa",
          minHeight: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          color: "#888",
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={e => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault()
          setDragging(false)
          onFile(e.dataTransfer.files[0])
        }}
      >
        <input
          type="file"
          accept={accept}
          style={{ display: "none" }}
          ref={inputRef}
          onChange={e => onFile(e.target.files[0])}
        />
        <Box sx={{ color: "#888", textWrap: "balance" }}>
          {i18n.t("trademarkSettings.pdfCoverDrop")}
        </Box>
      </Box>
    )}
  </>
)

ImageDropZone.propTypes = {
  preview: PropTypes.string,
  onFile: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  accept: PropTypes.string.isRequired,
  dragging: PropTypes.bool.isRequired,
  setDragging: PropTypes.func.isRequired,
  inputRef: PropTypes.object.isRequired,
  removeLabel: PropTypes.string.isRequired,
  uploading: PropTypes.bool,
}

const FileDropZone = ({
  fileName,
  onFile,
  onRemove,
  accept,
  dragging,
  setDragging,
  inputRef,
  removeLabel,
  uploading,
}) => (
  <>
    {uploading ? (
      <UploadingBox />
    ) : fileName ? (
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 2,
          background: "#fafafa",
          minHeight: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ fontSize: 14, color: "#414141" }}>{fileName}</Box>
        <IconButton size="small" onClick={onRemove} aria-label={removeLabel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : (
      <Box
        sx={{
          border: dragging ? "2px dashed #4A90D9" : "2px dashed #aaa",
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          background: dragging ? "#f0f7ff" : "#fafafa",
          minHeight: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          color: "#888",
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={e => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault()
          setDragging(false)
          onFile(e.dataTransfer.files[0])
        }}
      >
        <input
          type="file"
          accept={accept}
          style={{ display: "none" }}
          ref={inputRef}
          onChange={e => onFile(e.target.files[0])}
        />
        <Box sx={{ color: "#888", textWrap: "balance" }}>
          {i18n.t("trademarkSettings.pdfCoverDrop")}
        </Box>
      </Box>
    )}
  </>
)

FileDropZone.propTypes = {
  fileName: PropTypes.string,
  onFile: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  accept: PropTypes.string.isRequired,
  dragging: PropTypes.bool.isRequired,
  setDragging: PropTypes.func.isRequired,
  inputRef: PropTypes.object.isRequired,
  removeLabel: PropTypes.string.isRequired,
  uploading: PropTypes.bool,
}

const fileNameFromUrl = url => {
  if (!url) return ""
  return decodeURIComponent(url.split("/").pop() || "")
}

const EditBrandPdfTab = ({ pdfInfo, setPdfInfo }) => {
  const coverInputRef = useRef(null)
  const signatureInputRef = useRef(null)
  const [draggingCover, setDraggingCover] = useState(false)
  const [draggingSignature, setDraggingSignature] = useState(false)
  const { generateFileUrl, uploading } = useUtils()

  const handleCoverFile = async file => {
    if (file && file.type === "application/pdf") {
      const url = await generateFileUrl(file, "protected")
      if (url) setPdfInfo(prev => ({ ...prev, cover: url }))
    }
  }

  const handleSignatureFile = async file => {
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      const url = await generateFileUrl(file, "protected")
      if (url) setPdfInfo(prev => ({ ...prev, signature: url }))
    }
  }

  return (
    <Box>
      <Box sx={{ fontSize: 14, color: "#6d6d6d", mb: 3 }}>
        {i18n.t("trademarkSettings.pdfIntro")}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
            {i18n.t("trademarkSettings.pdfCoverTitle")}
          </Box>
          <FileDropZone
            fileName={fileNameFromUrl(pdfInfo.cover)}
            onFile={handleCoverFile}
            onRemove={() => setPdfInfo(prev => ({ ...prev, cover: "" }))}
            accept="application/pdf"
            dragging={draggingCover}
            setDragging={setDraggingCover}
            inputRef={coverInputRef}
            removeLabel={i18n.t("trademarkSettings.pdfRemoveCover")}
            uploading={uploading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
            {i18n.t("trademarkSettings.pdfSignatureTitle")}
          </Box>
          <ImageDropZone
            preview={pdfInfo.signature}
            onFile={handleSignatureFile}
            onRemove={() => setPdfInfo(prev => ({ ...prev, signature: "" }))}
            accept="image/png,image/jpeg"
            dragging={draggingSignature}
            setDragging={setDraggingSignature}
            inputRef={signatureInputRef}
            removeLabel={i18n.t("trademarkSettings.pdfRemoveSignature")}
            uploading={uploading}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
            {i18n.t("trademarkSettings.pdfConditionsTitle")}
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            size="small"
            variant="outlined"
            placeholder={i18n.t("trademarkSettings.pdfConditionsPlaceholder")}
            value={pdfInfo.conditions || ""}
            onChange={e =>
              setPdfInfo(prev => ({ ...prev, conditions: e.target.value }))
            }
          />
        </Grid>
      </Grid>
    </Box>
  )
}

EditBrandPdfTab.propTypes = {
  pdfInfo: PropTypes.shape({
    cover: PropTypes.string,
    signature: PropTypes.string,
    conditions: PropTypes.string,
  }).isRequired,
  setPdfInfo: PropTypes.func.isRequired,
}

export default EditBrandPdfTab
